import { useEffect, useRef, useState } from "react";
import Globe from "react-globe.gl";

interface UserPoint {
  id?: number;
  name: string;
  username: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  avatar?: string;
}

interface ClusterPoint extends UserPoint {
  isCluster?: boolean;
  clusterSize?: number;
  clusterUsers?: UserPoint[];
}

export default function CommunityGlobeJson() {
  const globeEl = useRef<any>();
  const [users, setUsers] = useState<ClusterPoint[]>([]);
  const [selected, setSelected] = useState<ClusterPoint | null>(null);
  const [clusterView, setClusterView] = useState<UserPoint[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const [pointSize, setPointSize] = useState(0.6);
  const autoRotateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Function to cluster points by location
  const clusterPointsByLocation = (points: UserPoint[]): ClusterPoint[] => {
    const locationMap = new Map<string, UserPoint[]>();
    
    // Group users by city + country
    points.forEach((point) => {
      const key = `${point.city},${point.country}`;
      if (!locationMap.has(key)) {
        locationMap.set(key, []);
      }
      locationMap.get(key)!.push(point);
    });
    
    const clustered: ClusterPoint[] = [];
    
    locationMap.forEach((locationUsers, key) => {
      if (locationUsers.length >= 3) {
        // Create a cluster point
        const firstUser = locationUsers[0];
        clustered.push({
          ...firstUser,
          isCluster: true,
          clusterSize: locationUsers.length,
          clusterUsers: locationUsers,
          name: `${firstUser.city}`,
          username: `${locationUsers.length} members`,
        });
      } else {
        // Add individual users
        clustered.push(...locationUsers);
      }
    });
    
    return clustered;
  };

  // Function to separate close points
  const separateClosePoints = (points: any[]) => {
    const separated = [...points];
    const minDistance = 0.5; // Minimum distance between points in degrees
    
    for (let i = 0; i < separated.length; i++) {
      for (let j = i + 1; j < separated.length; j++) {
        const latDiff = separated[i].latitude - separated[j].latitude;
        const lngDiff = separated[i].longitude - separated[j].longitude;
        const distance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
        
        if (distance < minDistance && !separated[i].isCluster && !separated[j].isCluster) {
          // Add small offset to separate overlapping points (but not clusters)
          const offset = (minDistance - distance) / 2;
          const angle = Math.atan2(latDiff, lngDiff);
          separated[j].latitude -= offset * Math.sin(angle);
          separated[j].longitude -= offset * Math.cos(angle);
          separated[j]._adjusted = true; // Mark as adjusted
        }
      }
    }
    
    return separated;
  };

  // Handle zoom changes to adjust point size
  const handleZoom = () => {
    const globe = globeEl.current;
    if (globe) {
      const camera = globe.camera();
      const distance = camera.position.length();
      
      // Calculate point size based on camera distance
      // Closer = smaller points, Further = larger points
      // Distance typically ranges from ~150 (close) to ~1000 (far)
      const minSize = 0.15;
      const maxSize = 0.7;
      const minDistance = 150;
      const maxDistance = 400;
      
      const normalizedDistance = Math.max(minDistance, Math.min(maxDistance, distance));
      const sizeScale = (normalizedDistance - minDistance) / (maxDistance - minDistance);
      const newSize = minSize + (sizeScale * (maxSize - minSize));
      
      setPointSize(newSize);
      handleInteraction();
    }
  };

  // Handle user interaction
  const handleInteraction = () => {
    const globe = globeEl.current;
    if (globe) {
      const controls = globe.controls();
      controls.autoRotate = false;
      setIsAutoRotating(false);
      
      // Clear existing timeout
      if (autoRotateTimeoutRef.current) {
        clearTimeout(autoRotateTimeoutRef.current);
      }
      
      // Resume auto-rotation after 5 seconds of inactivity
      autoRotateTimeoutRef.current = setTimeout(() => {
        if (globe && globe.controls()) {
          globe.controls().autoRotate = true;
          setIsAutoRotating(true);
        }
      }, 5000);
    }
  };

  useEffect(() => {
    fetch("https://cbuxrdarzwtolyjhnhzw.supabase.co/storage/v1/object/public/globe-cache/community_map.json")
      .then(res => res.json())
      .then(json => {
        const users = Array.isArray(json) ? json : json.data;
        
        // Enrich with avatars and ensure coordinates are numbers
        const enriched = users.map((u: any) => ({
          ...u,
          latitude: Number(u.latitude),
          longitude: Number(u.longitude),
          avatar:
            u.avatar ||
            `https://api.dicebear.com/9.x/avataaars/svg?seed=${encodeURIComponent(
              u.username || u.name || u.city
            )}`,
        }));
        
        // Cluster points by location
        const clustered = clusterPointsByLocation(enriched);
        
        // Separate close points for better clickability
        const separated = separateClosePoints(clustered);
        
        setUsers(separated);
        setIsLoading(false);

        const globe = globeEl.current;
        if (globe) {
          const controls = globe.controls();
          controls.autoRotate = true;
          controls.autoRotateSpeed = 0.6;
          controls.enableZoom = true;
          
          // Add interaction listeners
          controls.addEventListener('start', handleInteraction);
        }
      })
      .catch(err => {
        setIsLoading(false);
      });

    // Cleanup
    return () => {
      if (autoRotateTimeoutRef.current) {
        clearTimeout(autoRotateTimeoutRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div 
        className="h-screen w-full flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #0a0a0f 0%, #1a0a0a 50%, #0a0a0f 100%)" }}
      >
        <div className="text-center">
          <div 
            className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 mx-auto mb-4"
            style={{ borderColor: "#E67E22" }}
          />
          <p className="text-white" style={{ fontFamily: "'Bensoon', sans-serif" }}>
            Loading community map...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="relative h-screen w-screen"
      style={{ 
        background: "linear-gradient(135deg, #0a0a0f 0%, #1a0a0a 50%, #0a0a0f 100%)",
      }}
    >
      {/* Title and Stats */}
      <div className="absolute top-8 left-0 right-0 z-10 text-center px-4">
        <h1 
          className="text-4xl md:text-5xl font-bold text-white mb-4"
          style={{ 
            fontFamily: "'Bensoon', sans-serif",
            textShadow: "0 4px 20px rgba(0, 0, 0, 0.8)" 
          }}
        >
          Our <span style={{ color: "#E67E22" }}>Global</span> Community
        </h1>
        
        <div className="flex flex-wrap justify-center gap-6 text-white">
          <div>
            <span 
              className="text-3xl font-bold"
              style={{ color: "#E67E22" }}
            >
              {users.reduce((sum, u) => sum + (u.isCluster ? u.clusterSize! : 1), 0)}
            </span>
            <span className="ml-2 text-lg" style={{ color: "rgba(255, 255, 255, 0.7)" }}>
              members
            </span>
          </div>
          
          <div>
            <span 
              className="text-3xl font-bold"
              style={{ color: "#E67E22" }}
            >
              {new Set(users.map(p => p.country)).size}
            </span>
            <span className="ml-2 text-lg" style={{ color: "rgba(255, 255, 255, 0.7)" }}>
              countries
            </span>
          </div>
          
          <div>
            <span 
              className="text-3xl font-bold"
              style={{ color: "#E67E22" }}
            >
              {new Set(users.map(p => p.city)).size}
            </span>
            <span className="ml-2 text-lg" style={{ color: "rgba(255, 255, 255, 0.7)" }}>
              cities
            </span>
          </div>
          
          <div>
            <span 
              className="text-3xl font-bold"
              style={{ color: "#E67E22" }}
            >
              {users.filter(u => u.isCluster).length}
            </span>
            <span className="ml-2 text-lg" style={{ color: "rgba(255, 255, 255, 0.7)" }}>
              hotspots
            </span>
          </div>
        </div>
      </div>

      {/* Globe */}
      <Globe
        ref={globeEl}
        globeImageUrl="//unpkg.com/three-globe/example/img/earth-dark.jpg"
        bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
        backgroundColor="rgba(0,0,0,0)"
        pointsData={users}
        pointLat={(d: any) => d.latitude}
        pointLng={(d: any) => d.longitude}
        pointAltitude={(d: any) => {
          if (d.isCluster) return 0.005;
          return d._adjusted ? 0.003 : 0.001;
        }}
        pointColor={(d: any) => {
          if (d.isCluster) return "#D35400"; // Darker orange for clusters
          return d._adjusted ? "#F39C12" : "#E67E22";
        }}
        pointRadius={(d: any) => {
          if (d.isCluster) return pointSize * 1.5; // Larger for clusters
          return pointSize;
        }}
        pointResolution={4}
        pointLabel={(d: any) => {
          if (d.isCluster) {
            return `
              <div style="
                background: rgba(0, 0, 0, 0.9);
                padding: 12px 16px;
                border-radius: 8px;
                border: 2px solid #D35400;
                color: white;
                font-family: 'Inter', sans-serif;
                max-width: 250px;
              ">
                <div style="font-weight: bold; color: #E67E22; font-size: 16px; margin-bottom: 8px;">📍 ${d.city}, ${d.country}</div>
                <div style="font-size: 14px; opacity: 0.9; margin-bottom: 8px;">
                  <span style="background: #E67E22; padding: 4px 8px; border-radius: 4px; font-weight: bold;">${d.clusterSize} members</span>
                </div>
                <div style="font-size: 12px; opacity: 0.7; font-style: italic;">Click to view all members</div>
              </div>
            `;
          }
          return `
            <div style="
              background: rgba(0, 0, 0, 0.9);
              padding: 8px 12px;
              border-radius: 8px;
              border: 1px solid #E67E22;
              color: white;
              font-family: 'Inter', sans-serif;
              max-width: 200px;
            ">
              <img src="${d.avatar}" style="width: 40px; height: 40px; border-radius: 50%; margin-bottom: 8px; display: block;" />
              <div style="font-weight: bold; color: #E67E22;">${d.name}</div>
              <div style="font-size: 12px; opacity: 0.8;">@${d.username}</div>
              <div style="font-size: 12px; margin-top: 4px;">📍 ${d.city}, ${d.country}</div>
              ${d._adjusted ? '<div style="font-size: 10px; margin-top: 4px; opacity: 0.6; font-style: italic;">(position adjusted for visibility)</div>' : ''}
            </div>
          `;
        }}
        onPointClick={(d: any) => {
          handleInteraction();
          if (d.isCluster) {
            setClusterView(d.clusterUsers);
          } else {
            setSelected(d);
          }
        }}
        onZoom={handleZoom}
        atmosphereColor="#E67E22"
        atmosphereAltitude={0.25}
      />


      {/* Cluster Table View Modal */}
      {clusterView && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ 
            background: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(10px)",
          }}
          onClick={() => setClusterView(null)}
        >
          <div 
            className="relative rounded-xl p-6 text-white shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-auto"
            style={{
              background: "linear-gradient(135deg, rgba(20, 20, 25, 0.98) 0%, rgba(40, 40, 50, 0.98) 100%)",
              border: "2px solid #E67E22",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 
                  className="text-2xl font-bold mb-2"
                  style={{ fontFamily: "'Bensoon', sans-serif", color: "#E67E22" }}
                >
                  📍 {clusterView[0]?.city}, {clusterView[0]?.country}
                </h2>
                <p 
                  className="text-sm opacity-70"
                  style={{ fontFamily: "'Bensoon', sans-serif" }}
                >
                  {clusterView.length} community members
                </p>
              </div>
              <button
                onClick={() => setClusterView(null)}
                className="text-white/60 hover:text-white transition-colors"
                style={{ 
                  fontSize: "32px",
                  lineHeight: "1",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full" style={{ fontFamily: "'Bensoon', sans-serif" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #E67E22" }}>
                    <th className="text-left py-3 px-4" style={{ color: "#E67E22", fontWeight: 600 }}>Avatar</th>
                    <th className="text-left py-3 px-4" style={{ color: "#E67E22", fontWeight: 600 }}>Name</th>
                    <th className="text-left py-3 px-4" style={{ color: "#E67E22", fontWeight: 600 }}>Username</th>
                    <th className="text-left py-3 px-4" style={{ color: "#E67E22", fontWeight: 600 }}>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {clusterView.map((user, index) => (
                    <tr 
                      key={`${user.username}-${index}`}
                      className="hover:bg-white/5 transition-colors"
                      style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
                    >
                      <td className="py-3 px-4">
                        <img
                          src={user.avatar}
                          alt={user.name}
                          className="w-10 h-10 rounded-full"
                          style={{ border: "2px solid #E67E22" }}
                        />
                      </td>
                      <td className="py-3 px-4 font-medium">{user.name}</td>
                      <td className="py-3 px-4 opacity-80">@{user.username}</td>
                      <td className="py-3 px-4 opacity-70 text-sm">{user.city}, {user.country}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Selected User Card */}
      {selected && !selected.isCluster && (
        <div 
          className="absolute bottom-8 right-8 rounded-xl p-4 text-white shadow-lg w-64"
          style={{
            background: "linear-gradient(135deg, rgba(20, 20, 25, 0.95) 0%, rgba(40, 40, 50, 0.95) 100%)",
            border: "1px solid #E67E22",
            backdropFilter: "blur(10px)",
          }}
        >
          <div className="flex items-center mb-3">
            <img
              src={selected.avatar}
              alt={selected.name}
              className="w-10 h-10 rounded-full mr-3"
              style={{ border: "2px solid #E67E22" }}
            />
            <div>
              <div 
                className="font-semibold"
                style={{ fontFamily: "'Bensoon', sans-serif" }}
              >
                {selected.name}
              </div>
              <div 
                className="text-xs"
                style={{ 
                  color: "#E67E22",
                  fontFamily: "'Bensoon', sans-serif" 
                }}
              >
                @{selected.username}
              </div>
            </div>
          </div>
          <div 
            className="text-sm opacity-80"
            style={{ fontFamily: "'Bensoon', sans-serif" }}
          >
            📍 {selected.city}, {selected.country}
          </div>
          <button
            onClick={() => setSelected(null)}
            className="absolute top-2 right-2 hover:text-white"
            style={{ 
              color: "rgba(255, 255, 255, 0.6)",
              fontSize: "24px",
              lineHeight: "1",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Instructions */}
      <div className="absolute bottom-8 left-0 right-0 z-10 text-center px-4">
        <p 
          className="text-white/60 text-sm"
          style={{ fontFamily: "'Bensoon', sans-serif" }}
        >
          Drag to rotate • Scroll to zoom • Click dots for details • Larger dots = multiple members
        </p>
        <p 
          className="text-white/40 text-xs mt-1"
          style={{ fontFamily: "'Bensoon', sans-serif" }}
        >
          {pointSize < 0.3 ? "🔍 Zoomed in - points scaled down" : pointSize > 0.5 ? "🌍 Zoomed out - full view" : ""}
        </p>
        {!isAutoRotating && (
          <p 
            className="text-white/40 text-xs mt-1"
            style={{ fontFamily: "'Bensoon', sans-serif" }}
          >
            Auto-rotation paused • Will resume in 5s
          </p>
        )}
      </div>

      {/* Auto-rotation Toggle Button */}
      <button
        onClick={() => {
          const globe = globeEl.current;
          if (globe) {
            const controls = globe.controls();
            controls.autoRotate = !isAutoRotating;
            setIsAutoRotating(!isAutoRotating);
            
            if (autoRotateTimeoutRef.current) {
              clearTimeout(autoRotateTimeoutRef.current);
              autoRotateTimeoutRef.current = null;
            }
          }
        }}
        className="absolute top-28 right-8 z-10 p-3 rounded-full hover:scale-110 transition-transform"
        style={{
          background: "rgba(20, 20, 25, 0.8)",
          border: "1px solid #E67E22",
          backdropFilter: "blur(10px)",
        }}
        title={isAutoRotating ? "Pause rotation" : "Resume rotation"}
      >
        <span style={{ fontSize: "20px" }}>
          {isAutoRotating ? "⏸️" : "▶️"}
        </span>
      </button>
    </div>
  );
}

